import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { CheckCircle2, AlertCircle, AlertTriangle, Save, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ReviewSubmission() {
  const { submission_id } = useParams();
  const navigate = useNavigate();
  
  const [submission, setSubmission] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedMarks, setEditedMarks] = useState({});
  const [editedTranscription, setEditedTranscription] = useState('');
  const [transcriptionEdited, setTranscriptionEdited] = useState(false);

  useEffect(() => {
    fetchSubmission();
  }, [submission_id]);

  const fetchSubmission = async () => {
    try {
      setLoading(true);
      
      // Get all projects to find the one with this submission
      const projectsResponse = await axios.get(`${API}/marking/projects`, { withCredentials: true });
      const allProjects = projectsResponse.data.projects;
      
      let foundSubmission = null;
      let foundProject = null;
      
      for (const proj of allProjects) {
        const projectDetail = await axios.get(`${API}/marking/projects/${proj.project_id}`, { withCredentials: true });
        const sub = projectDetail.data.submissions?.find(s => s.submission_id === submission_id);
        
        if (sub) {
          foundSubmission = sub;
          foundProject = projectDetail.data;
          break;
        }
      }
      
      if (!foundSubmission || !foundProject) {
        toast.error('Submission not found');
        navigate('/assessment');
        return;
      }
      
      setSubmission(foundSubmission);
      setProject(foundProject);
      setEditedTranscription(foundSubmission.transcription || '');
    } catch (error) {
      console.error('Error fetching submission:', error);
      toast.error('Failed to load submission');
      navigate('/assessment');
    } finally {
      setLoading(false);
    }
  };

  const getConfidenceBadge = (confidence) => {
    if (confidence >= 80) {
      return {
        color: 'bg-green-100 text-green-700 border-green-300',
        label: 'High Confidence ✓',
        icon: CheckCircle2,
        bgColor: 'bg-green-50'
      };
    } else if (confidence >= 60) {
      return {
        color: 'bg-amber-100 text-amber-700 border-amber-300',
        label: 'Review Recommended',
        icon: AlertTriangle,
        bgColor: 'bg-amber-50'
      };
    } else {
      return {
        color: 'bg-red-100 text-red-700 border-red-300',
        label: 'Manual Review Required',
        icon: AlertCircle,
        bgColor: 'bg-red-50'
      };
    }
  };

  const handleMarkEdit = (questionNumber, field, value) => {
    setEditedMarks({
      ...editedMarks,
      [questionNumber]: {
        ...editedMarks[questionNumber],
        [field]: value
      }
    });
  };

  const saveReview = async () => {
    try {
      setSaving(true);
      
      // Save transcription if edited
      if (transcriptionEdited && editedTranscription !== submission.transcription) {
        await axios.put(
          `${API}/marking/submissions/${submission_id}/transcription`,
          { transcription: editedTranscription },
          { withCredentials: true }
        );
      }
      
      // Save marks if edited
      const reviews = Object.entries(editedMarks).map(([questionNumber, edits]) => ({
        question_number: parseInt(questionNumber),
        corrected_mark: parseFloat(edits.mark),
        corrected_feedback: edits.feedback
      }));

      if (reviews.length > 0) {
        await axios.put(
          `${API}/marking/submissions/${submission_id}/review`,
          reviews,
          { withCredentials: true }
        );
      }
      
      if (reviews.length === 0 && !transcriptionEdited) {
        toast.error('No changes to save');
        return;
      }
      
      toast.success('Review saved successfully!');
      fetchSubmission();
      setEditedMarks({});
      setTranscriptionEdited(false);
    } catch (error) {
      console.error('Error saving review:', error);
      toast.error('Failed to save review');
    } finally {
      setSaving(false);
    }
  };

  const approveMarks = async () => {
    try {
      await axios.post(
        `${API}/marking/submissions/${submission_id}/approve`,
        {},
        { withCredentials: true }
      );
      toast.success('Marks approved and finalized!');
      navigate(`/assessment/project/${project.project_id}`);
    } catch (error) {
      console.error('Error approving marks:', error);
      toast.error('Failed to approve marks');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navigation />
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-lg text-slate-600">Loading submission...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!submission || !project) return null;

  const greenAnswers = submission.answers?.filter(a => a.confidence_score >= 80) || [];
  const amberAnswers = submission.answers?.filter(a => a.confidence_score >= 60 && a.confidence_score < 80) || [];
  const redAnswers = submission.answers?.filter(a => a.confidence_score < 60) || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation />
      
      <section className="py-16 px-6 sm:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate(`/assessment/project/${project.project_id}`)}
              className="text-purple-600 hover:text-purple-700 font-medium mb-4 inline-flex items-center gap-2"
            >
              ← Back to Project
            </button>
            
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-bold text-slate-900 mb-2">{submission.student_name}</h1>
                <p className="text-base text-slate-600">{project.project_name}</p>
              </div>
              
              <div className="text-right">
                <div className="text-4xl font-bold text-purple-600 mb-1">
                  {submission.total_marks?.toFixed(1) || 0} / {project.total_marks}
                </div>
                <div className="text-lg font-semibold text-slate-600">
                  {submission.percentage?.toFixed(1) || 0}%
                </div>
              </div>
            </div>
          </div>

          {/* AI Disclaimer */}
          <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4 mb-8">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <p className="font-semibold mb-1">AI-Suggested Marking — Teacher Approval Required</p>
                <p>All marks below are AI-generated suggestions. Please review each answer carefully before approving final marks.</p>
              </div>
            </div>
          </div>

          {/* Unreadable Warning (if applicable) */}
          {submission.ocr_status === 'unreadable' && (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 mb-8">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-red-900">
                  <p className="font-semibold mb-1">⚠️ Unreadable Submission</p>
                  <p>The AI could not confidently read this paper (confidence &lt; 65%). Please manually review and transcribe the student's answers below.</p>
                </div>
              </div>
            </div>
          )}

          {/* Full Transcription */}
          {submission.transcription && (
            <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-6 mb-8">
              <h2 className="text-xl font-bold text-slate-900 mb-3">
                Full Transcription
                {submission.ocr_status === 'unreadable' && (
                  <span className="ml-2 text-sm font-normal text-red-600">(Editable - Low Confidence)</span>
                )}
              </h2>
              {submission.ocr_status === 'unreadable' ? (
                <textarea
                  value={editedTranscription}
                  onChange={(e) => {
                    setEditedTranscription(e.target.value);
                    setTranscriptionEdited(true);
                  }}
                  rows={10}
                  className="w-full rounded-lg border-2 border-slate-200 px-4 py-3 text-sm focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none font-mono"
                  placeholder="Manually type the student's answers here..."
                />
              ) : (
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{submission.transcription}</p>
                </div>
              )}
              {submission.confidence_score !== undefined && (
                <p className="text-xs text-slate-500 mt-2">
                  Overall OCR Confidence: {submission.confidence_score}%
                </p>
              )}
              {transcriptionEdited && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mt-3">
                  <p className="text-sm text-purple-900">
                    ✏️ <strong>Transcription edited.</strong> Don't forget to save your changes below.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* AI Feedback */}
          {submission.ai_feedback && (
            <div className="bg-purple-50 rounded-2xl border-2 border-purple-200 shadow-sm p-6 mb-8">
              <h2 className="text-xl font-bold text-purple-900 mb-3">AI Personalized Feedback</h2>
              <p className="text-sm text-purple-900">{submission.ai_feedback}</p>
            </div>
          )}

          {/* Confidence Summary */}
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-green-900 mb-1">High Confidence</p>
                  <p className="text-2xl font-bold text-green-700">{greenAnswers.length}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-amber-900 mb-1">Review Recommended</p>
                  <p className="text-2xl font-bold text-amber-700">{amberAnswers.length}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-amber-600" />
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-red-900 mb-1">Manual Review</p>
                  <p className="text-2xl font-bold text-red-700">{redAnswers.length}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
            </div>
          </div>

          {/* Answers Review */}
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-slate-900">Question-by-Question Review</h2>

            {submission.answers?.sort((a, b) => a.question_number - b.question_number).map((answer) => {
              const badge = getConfidenceBadge(answer.confidence_score);
              const Icon = badge.icon;
              const edited = editedMarks[answer.question_number];
              const currentMark = edited?.mark !== undefined ? edited.mark : answer.suggested_mark;
              const currentFeedback = edited?.feedback !== undefined ? edited.feedback : answer.feedback;

              return (
                <div key={answer.question_number} className={`bg-white rounded-2xl border-2 ${badge.color.split(' ')[2] ? `border-${badge.color.split(' ')[2].split('-')[1]}-200` : 'border-slate-200'} shadow-sm overflow-hidden`}>
                  <div className={`${badge.bgColor} px-6 py-4 border-b border-slate-200`}>
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-bold text-slate-900">Question {answer.question_number}</h3>
                      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold border ${badge.color}`}>
                        <Icon className="w-4 h-4" />
                        {badge.label}
                      </span>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">Confidence: {answer.confidence_score}%</p>
                  </div>

                  <div className="p-6 space-y-4">
                    <div>
                      <p className="text-sm font-semibold text-slate-700 mb-2">Student's Answer:</p>
                      <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                        <p className="text-base text-slate-900">{answer.extracted_text}</p>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                          Marks <span className="text-slate-500">(Max: {answer.max_mark})</span>
                        </label>
                        <input
                          type="number"
                          value={currentMark}
                          onChange={(e) => handleMarkEdit(answer.question_number, 'mark', e.target.value)}
                          min="0"
                          max={answer.max_mark}
                          step="0.5"
                          className="w-full rounded-lg border-2 border-slate-200 px-4 py-2 text-lg font-semibold focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">AI Feedback</label>
                        <textarea
                          value={currentFeedback}
                          onChange={(e) => handleMarkEdit(answer.question_number, 'feedback', e.target.value)}
                          rows={3}
                          className="w-full rounded-lg border-2 border-slate-200 px-4 py-2 text-sm focus:border-purple-500 focus:ring-4 focus:ring-purple-100 outline-none"
                        />
                      </div>
                    </div>

                    {edited && (
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                        <p className="text-sm text-purple-900 font-medium">✏️ Modified by teacher</p>
                      </div>
                    )}

                    {answer.review_required && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-sm text-red-900 font-medium">
                          ⚠️ This answer requires manual review before approval
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Action Buttons */}
          <div className="sticky bottom-6 mt-8 bg-white rounded-2xl border-2 border-slate-200 p-6 shadow-xl">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm text-slate-600 mb-1">Total Marks</p>
                <p className="text-2xl font-bold text-slate-900">
                  {submission.total_marks?.toFixed(1) || 0} / {project.total_marks}
                </p>
              </div>

              <div className="flex gap-4">
                {Object.keys(editedMarks).length > 0 && (
                  <button
                    onClick={saveReview}
                    disabled={saving}
                    className="bg-blue-600 text-white hover:bg-blue-700 px-6 py-3 rounded-xl font-semibold transition-all inline-flex items-center gap-2 disabled:bg-slate-300"
                  >
                    <Save className="w-5 h-5" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                )}

                <button
                  onClick={approveMarks}
                  disabled={Object.keys(editedMarks).length > 0}
                  className={`px-6 py-3 rounded-xl font-semibold transition-all inline-flex items-center gap-2 ${
                    Object.keys(editedMarks).length > 0
                      ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700 shadow-lg active:scale-95'
                  }`}
                >
                  <Check className="w-5 h-5" />
                  Approve Final Marks
                </button>
              </div>
            </div>

            {Object.keys(editedMarks).length > 0 && (
              <p className="text-sm text-amber-600 mt-4">
                ⚠️ Save your changes before approving final marks
              </p>
            )}
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}

export default ReviewSubmission;
